import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { ArrowLeft, Clock, Trash2, BarChart3, Search, MessageSquare, Star, X } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { Input } from "../components/ui/input";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const History = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Lock body scroll when modal is open (critical for iOS/Android WebView)
  useEffect(() => {
    if (deleteTarget) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => { document.body.style.overflow = ""; document.body.style.touchAction = ""; };
  }, [deleteTarget]);

  const canFavorite = user?.plan === "decide" || user?.plan === "avanza";
  const dateLocaleMap = { de: "de-DE", it: "it-IT", en: "en-GB", es: "es-ES" };
  const dateLang = dateLocaleMap[lang] || "es-ES";

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/futura/history`, { withCredentials: true });
        setConsultations(response.data);
      } catch (error) { console.error("History fetch failed:", error); }
      finally { setLoading(false); }
    };
    fetchHistory();
  }, []);

  const openDeleteModal = (consultationId, e) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteTarget(consultationId);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/futura/consultation/${deleteTarget}`, { withCredentials: true });
      setConsultations(prev => prev.filter(c => c.consultation_id !== deleteTarget));
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleFavorite = async (consultationId, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/futura/consultation/${consultationId}/favorite`, {}, { withCredentials: true });
      setConsultations(prev => prev.map(c =>
        c.consultation_id === consultationId ? { ...c, is_favorite: res.data.is_favorite } : c
      ));
    } catch (error) { console.error("Favorite toggle failed:", error); }
  };

  const filtered = consultations.filter(item => {
    const matchesSearch = item.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.response?.recommendation?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFav = showFavoritesOnly ? item.is_favorite : true;
    return matchesSearch && matchesFav;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      {/* Delete confirmation modal — WebView/Android/iOS safe (no window.confirm) */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          onClick={() => !deleting && setDeleteTarget(null)}
          onTouchMove={(e) => e.preventDefault()}
          data-testid="delete-modal-overlay"
        >
          <div
            className="bg-[#1a1a2e] border border-white/10 rounded-t-3xl sm:rounded-2xl p-6 pb-8 sm:pb-6 w-full sm:max-w-sm mx-0 sm:mx-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-5 sm:hidden" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] font-semibold text-lg">{t("history_delete_title")}</h3>
              <button
                onClick={() => !deleting && setDeleteTarget(null)}
                className="text-white/40 hover:text-white active:text-white p-2 -m-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-white/60 text-sm mb-6">{t("history_delete_confirm")}</p>
            <div className="flex gap-3">
              <Button
                onClick={() => !deleting && setDeleteTarget(null)}
                variant="outline"
                className="flex-1 rounded-xl border-white/10 text-white hover:bg-white/5 active:bg-white/10 min-h-[48px] text-base"
                data-testid="delete-cancel-btn"
              >
                {t("history_delete_cancel")}
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 active:bg-red-700 text-white min-h-[48px] text-base"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <span className="animate-spin">...</span> : t("history_delete_yes")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <header className="relative z-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">{t("nav_back")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="dark" />
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-lg">FUTURA</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10 animate-fade-in flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold tracking-tight mb-2">{t("history_title")}</h1>
            <p className="text-white/50">{t("history_subtitle")}</p>
          </div>
          {user?.plan === "avanza" && (
            <Button onClick={() => navigate("/comparador")} className="bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-full px-5 py-2.5 font-medium text-sm whitespace-nowrap" data-testid="comparador-btn">
              <BarChart3 className="w-4 h-4 mr-2" />{t("history_compare_btn")}
            </Button>
          )}
        </div>

        <div className="mb-8 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t("history_search")} className="pl-12 h-14 bg-white/5 border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:border-[#002FA7]/50" data-testid="history-search" />
            </div>
            {canFavorite && (
              <button onClick={() => setShowFavoritesOnly(!showFavoritesOnly)} className={`h-14 px-4 rounded-2xl border transition-all flex items-center gap-2 ${showFavoritesOnly ? "bg-amber-500/10 border-amber-500/30 text-amber-400" : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"}`} data-testid="favorites-filter-btn">
                <Star className={`w-5 h-5 ${showFavoritesOnly ? "fill-amber-400" : ""}`} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (<div key={i} className="bg-white/5 rounded-2xl p-6 animate-pulse"><div className="h-5 bg-white/10 rounded w-2/3 mb-3" /><div className="h-4 bg-white/10 rounded w-1/2" /></div>))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-16 text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6"><MessageSquare className="w-8 h-8 text-white/30" /></div>
            <h3 className="font-['Outfit'] text-xl font-semibold mb-2">{searchTerm ? t("history_no_results") : t("history_no_consultations")}</h3>
            <p className="text-white/50 mb-8">{searchTerm ? t("history_no_results_hint") : t("history_no_consultations_hint")}</p>
            {!searchTerm && (
              <Button onClick={() => navigate("/dashboard")} className="bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 text-white rounded-full px-8" data-testid="empty-new-btn">{t("history_first_btn")}</Button>
            )}
          </div>
        ) : (
          <div className="space-y-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            {filtered.map((item, index) => (
              <div key={item.consultation_id} onClick={() => navigate(`/history/${item.consultation_id}`)} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all cursor-pointer group" data-testid={`history-item-${index}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white mb-2 truncate">{item.question}</p>
                    <p className="text-white/60 text-sm line-clamp-2 mb-3">&#10003; {item.response?.recommendation}</p>
                    <div className="flex items-center gap-4 text-sm text-white/40">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(item.created_at).toLocaleDateString(dateLang, { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                    {canFavorite && (
                      <Button variant="ghost" size="icon" onClick={(e) => handleToggleFavorite(item.consultation_id, e)} className={`${item.is_favorite ? "text-amber-400" : "text-white/40 hover:text-amber-400 sm:opacity-0 sm:group-hover:opacity-100"} hover:bg-amber-500/10 active:bg-amber-500/10 transition-all w-10 h-10 min-w-[44px] min-h-[44px]`} data-testid={`fav-btn-${index}`}>
                        <Star className={`w-5 h-5 ${item.is_favorite ? "fill-amber-400" : ""}`} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={(e) => openDeleteModal(item.consultation_id, e)} onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); openDeleteModal(item.consultation_id, e); }} className="text-white/40 hover:text-[#EF4444] active:text-[#EF4444] hover:bg-[#EF4444]/10 active:bg-[#EF4444]/10 transition-all sm:opacity-0 sm:group-hover:opacity-100 w-10 h-10 min-w-[44px] min-h-[44px]" data-testid={`delete-btn-${index}`}>
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div><p className="text-white/40 mb-1">&#128202; {t("history_motives")}</p><p className="text-white/70">{item.response?.motives?.length || 0} {t("history_reasons")}</p></div>
                    <div><p className="text-white/40 mb-1">&#9888; {t("history_risks")}</p><p className="text-white/70">{item.response?.risks?.length || 0} {t("history_identified")}</p></div>
                    <div><p className="text-white/40 mb-1">&#128260; {t("history_alternative")}</p><p className="text-white/70 truncate">{item.response?.alternative?.slice(0, 30)}...</p></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default History;
